import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  CircularProgress,
  Box,
  TextField,
} from "@mui/material";
import api from "src/services/api";
import useAuthenticate from "src/hooks/useAuthenticate";
import { toast } from "react-toastify";
import Swal from "sweetalert2";

interface TShirtData {
  id: number;
  measure: string;
  size: string;
  color: string;
  price: number;
}

interface EditShirtModalProps {
  open: boolean;
  onClose: () => void;
  subscriptionId: number;
  slug: string;
  currentShirt: TShirtData | null;
  onSuccess: (subscriptionId: number, newShirt: TShirtData | null) => void;
}

const SHIRT_SIZES = [
  "6 anos",
  "8 anos",
  "10 anos",
  "PP",
  "P",
  "M",
  "G",
  "GG",
  "XG",
];

export function EditShirtModal({
  open,
  onClose,
  subscriptionId,
  slug,
  currentShirt,
  onSuccess,
}: EditShirtModalProps) {
  const { getToken } = useAuthenticate();
  const [measure, setMeasure] = useState("");
  const [size, setSize] = useState("");
  const [color, setColor] = useState("");
  const [price, setPrice] = useState("");
  const [loading, setLoading] = useState(false);

  const handleOpen = () => {
    setMeasure(currentShirt?.measure || "");
    setSize(currentShirt?.size || "");
    setColor(currentShirt?.color || "");
    setPrice(currentShirt?.price != null ? String(currentShirt.price) : "");
  };

  const handleSave = async () => {
    if (!size) {
      toast.error("Selecione um tamanho");
      return;
    }
    if (!measure) {
      toast.error("Informe o modelo da camisa");
      return;
    }
    if (!color) {
      toast.error("Informe a cor da camisa");
      return;
    }

    setLoading(true);
    try {
      const tshirtData = {
        measure,
        size,
        color,
        price: price ? Number(price) : 0,
      };

      const response = await api.patch(
        `/tournaments/enrollments/${subscriptionId}`,
        { slug, tshirt: tshirtData },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getToken()}`,
          },
        }
      );

      if (response.status === 200 || response.status === 201) {
        toast.success(
          currentShirt
            ? "Camisa atualizada com sucesso!"
            : "Camisa adicionada com sucesso!"
        );
        onSuccess(subscriptionId, {
          id: currentShirt?.id || 0,
          ...tshirtData,
        });
        onClose();
      }
    } catch (error: any) {
      toast.error(
        error.response?.data?.message || "Erro ao salvar camisa"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async () => {
    onClose();

    const result = await Swal.fire({
      title: "Remover camisa?",
      text: "A camisa será desvinculada desta inscrição.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Sim, remover",
      cancelButtonText: "Cancelar",
    });

    if (!result.isConfirmed) return;

    try {
      const response = await api.patch(
        `/tournaments/enrollments/${subscriptionId}`,
        { slug, tshirt: null },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getToken()}`,
          },
        }
      );

      if (response.status === 200 || response.status === 201) {
        toast.success("Camisa removida com sucesso!");
        onSuccess(subscriptionId, null);
      }
    } catch (error: any) {
      toast.error(
        error.response?.data?.message || "Erro ao remover camisa"
      );
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      TransitionProps={{ onEnter: handleOpen }}
    >
      <DialogTitle>
        {currentShirt ? "Editar Camisa" : "Adicionar Camisa"}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
          {currentShirt && (
            <Typography variant="body2" color="text.secondary">
              <strong>Atual:</strong>{" "}
              {currentShirt.measure?.toUpperCase()} ({currentShirt.size?.toUpperCase()}) -{" "}
              {currentShirt.color?.toUpperCase()}
            </Typography>
          )}

          <TextField
            label="Modelo"
            value={measure}
            onChange={(e) => setMeasure(e.target.value)}
            size="small"
            fullWidth
          />

          <TextField
            label="Cor"
            value={color}
            onChange={(e) => setColor(e.target.value)}
            size="small"
            fullWidth
          />

          <FormControl fullWidth size="small">
            <InputLabel>Tamanho</InputLabel>
            <Select
              value={size}
              label="Tamanho"
              onChange={(e) => setSize(e.target.value)}
            >
              {SHIRT_SIZES.map((s) => (
                <MenuItem key={s} value={s}>
                  {s.toUpperCase()}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Preço"
            value={price}
            onChange={(e) =>
              setPrice(e.target.value.replace(/[^0-9.]/g, "").replace(/(\..*)\./g, "$1"))
            }
            size="small"
            fullWidth
            inputProps={{ inputMode: "decimal" }}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ justifyContent: currentShirt ? "space-between" : "flex-end", px: 3, pb: 2 }}>
        {currentShirt && (
          <Button
            onClick={handleRemove}
            disabled={loading}
            color="error"
            variant="outlined"
          >
            Remover camisa
          </Button>
        )}
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button onClick={onClose} disabled={loading}>
            Cancelar
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={loading}
            sx={{ backgroundColor: "#373737", "&:hover": { backgroundColor: "#555" } }}
          >
            {loading ? (
              <CircularProgress size={20} color="inherit" />
            ) : currentShirt ? (
              "Salvar"
            ) : (
              "Adicionar"
            )}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
